/**
 * activityNormalizer.js
 *
 * Sistema de normalización de actividades de perforación.
 * Cada categoría canónica tiene:
 *   - key:      identificador interno (nunca cambia)
 *   - color:    color del gráfico
 *   - labels:   traducción en ES / EN / PT
 *   - patterns: strings/substrings que se mapean a esta categoría
 *               (se compara contra el texto en minúsculas y sin tildes)
 *
 * Para agregar una variante nueva:
 *   1. Encontrá la categoría correcta abajo
 *   2. Agregá el string a su array `patterns`
 *   Eso es todo — el resto es automático.
 */

const normalize = (str) =>
    str
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .trim();

export const ACTIVITY_CATEGORIES = [
    {
        key: "drilling",
        color: "#1565C0",
        labels: { es: "Perforación", en: "Drilling", pt: "Perfuração" },
        patterns: [
            "perforacion",
            "perforación",
            "drilling",
            "perfuracao",
            "perfora",
            "perfo",
            "perf",
        ],
    },
    {
        key: "conditioning",
        color: "#26a69a",
        labels: { es: "Acondicionamiento", en: "Conditioning", pt: "Condicionamento" },
        patterns: [
            "acondicionamiento",
            "acondi",
            "conditioning",
            "condicionamento",
            "acondicionado",
            "acond",
        ],
    },
    {
        key: "lunch",
        color: "#ffa726",
        labels: { es: "Almuerzo", en: "Lunch", pt: "Almoço" },
        patterns: [
            "almuerzo",
            "comida",
            "lunch",
            "almoco",
            "almoço",
        ],
    },
    {
        key: "rod_extraction",
        color: "#ab47bc",
        labels: { es: "Extracción de barras", en: "Rod extraction", pt: "Extração de barras" },
        patterns: [
            "extraccion de barras",
            "extracción de barras",
            "izado de herramienta",
            "izado",
            "rod extraction",
            "extracao de barras",
            "extraccion",
            "sacado de barras",
            "izaje",
        ],
    },
    {
        key: "rod_insertion",
        color: "#ec407a",
        labels: { es: "Incorporación de barras", en: "Rod insertion", pt: "Incorporação de barras" },
        patterns: [
            "incorporacion de barras",
            "incorporación de barras",
            "introduccion de barras",
            "introducción de barras",
            "incorpora",
            "introducci",
            "rod insertion",
            "incorporacao de barras",
            "metida de barras",
            "ingreso de barras",
        ],
    },
    {
        key: "tool_maintenance",
        color: "#42a5f5",
        labels: { es: "Mantenimiento de herramienta", en: "Tool maintenance", pt: "Manutenção de ferramenta" },
        patterns: [
            "mantenimiento de herramienta",
            "mantenimiento de la herramienta",
            "mantenimieto de la herramienta",
            "tool maintenance",
            "manutencao de ferramenta",
            "manutenção de ferramenta",
            "mantenimiento herramienta",
            "mantenimientos de herramientas",
        ],
    },
    {
        key: "pump_maintenance",
        color: "#26c6da",
        labels: { es: "Mantenimiento de prensa", en: "Pump maintenance", pt: "Manutenção de bomba" },
        patterns: [
            "mantenimiento de prenza",
            "mantenimiento de prensa",
            "pump maintenance",
            "manutencao de bomba",
        ],
    },
    {
        key: "general_maintenance",
        color: "#78909c",
        labels: { es: "Mantenimiento general", en: "General maintenance", pt: "Manutenção geral" },
        patterns: [
            "mantenimiento",
            "maintenance",
            "manutencao",
            "manutenção",
        ],
    },
    {
        key: "bit_change",
        color: "#ff7043",
        labels: { es: "Cambio de corona", en: "Bit change", pt: "Troca de coroa" },
        patterns: [
            "cambio de corona",
            "cambio de corona y mantenimiento",
            "bit change",
            "troca de coroa",
        ],
    },
    {
        key: "shift_change",
        color: "#bdbdbd",
        labels: { es: "Cambio de turno", en: "Shift change", pt: "Troca de turno" },
        patterns: [
            "cambio de turno",
            "shift change",
            "troca de turno",
        ],
    },
    {
        key: "api_pit_cleaning",
        color: "#a5d6a7",
        labels: { es: "Limpieza/descarga de pileta API", en: "API pit cleaning", pt: "Limpeza de poço API" },
        patterns: [
            "descarga de pileta api",
            "descarga de la pileta api",
            "limpieza de pileta api",
            "limpieza de la pileta api",
            "mantenimiento de piletas api",
            "api pit cleaning",
            "limpeza de poco api",
            "limpeza de poço api",

            "limpieza de pileta",
            "limpieza de la pileta",
            "descarga de pileta",
            "descarga de la pileta",
        ],
    },
    {
        key: "tool_movement",
        color: "#ce93d8",
        labels: { es: "Movimiento de herramientas", en: "Tool movement", pt: "Movimentação de ferramentas" },
        patterns: [
            "movimiento de herramientas",
            "tool movement",
            "movimentacao de ferramentas",
        ],
    },
    {
        key: "recovery",
        color: "#ffcc02",
        labels: { es: "Maniobras de recuperación", en: "Recovery operations", pt: "Manobras de recuperação" },
        patterns: [
            "maniobras de recuperacion",
            "maniobras de recuperación",
            "recovery operations",
            "manobras de recuperacao",
            "manobras de recuperação",
        ],
    },
    {
        key: "standby_climate",
        color: "#90caf9",
        labels: { es: "Stand by climático", en: "Weather standby", pt: "Espera climática" },
        patterns: [
            "stand by clim",
            "standby clim",
            "standy clim",
            "stand by por viento",
            "stand by clima",
            "weather standby",
            "espera climatica",
            "espera clim",
        ],
    },
    {
        key: "standby",
        color: "#b0bec5",
        labels: { es: "Stand by", en: "Standby", pt: "Espera" },
        patterns: [
            "stand by",
            "standby",
            "espera",
            "suspension de actividades",
            "suspensión de actividades",
            "suspension",
            "suspensión",
            "stan day",
            "stand di",
        ],
    },
    {
        key: "other",
        color: "#eeeeee",
        labels: { es: "Otro", en: "Other", pt: "Outro" },
        patterns: [
            "capacitacion",
            "capc.",
            "training",
            "treinamento",
        ],
    },
];



const _patternMap = new Map();

for (const cat of ACTIVITY_CATEGORIES) {
    for (const pattern of cat.patterns) {
        _patternMap.set(normalize(pattern), cat.key);
    }
}

const _categoryByKey = Object.fromEntries(
    ACTIVITY_CATEGORIES.map((c) => [c.key, c])
);


/**
 * Dado el nombre crudo de una actividad, devuelve la categoría canónica.
 * @param {string} rawName
 * @returns {{ key, color, labels }} — categoría canónica
 */
export const normalizeActivity = (rawName) => {
    if (!rawName) return _categoryByKey["other"];

    const n = normalize(rawName);

    if (_patternMap.has(n)) {
        return _categoryByKey[_patternMap.get(n)];
    }

    for (const cat of ACTIVITY_CATEGORIES) {
        for (const pattern of cat.patterns) {
            if (n.includes(normalize(pattern))) {
                return cat;
            }
        }
    }

    return _categoryByKey["other"];
};

/**
 * Devuelve el label traducido de una actividad cruda.
 * @param {string} rawName
 * @param {"es"|"en"|"pt"} lang
 * @returns {string}
 */
export const getActivityLabel = (rawName, lang = "es") => {
    const cat = normalizeActivity(rawName);
    return cat.labels[lang] ?? cat.labels.es;
};

/**
 * Devuelve el color de una actividad cruda.
 * @param {string} rawName
 * @returns {string} hex color
 */
export const getActivityColor = (rawName) => {
    return normalizeActivity(rawName).color;
};

/**
 * Devuelve todas las categorías canónicas (útil para construir leyendas).
 * @returns {Array}
 */
export const getAllCategories = () => ACTIVITY_CATEGORIES;